import {afterNextRender, DestroyRef, Directive, effect, ElementRef, inject, input, Renderer2} from '@angular/core';
import {translate} from '@jsverse/transloco';
import {FormFieldComponent} from '../../components/form-field/form-field.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Directive({
  selector: 'ms-form-field[msRemainingCharacters]',
  standalone: true,
})
export class RemainingCharactersDirective {
  readonly maxLength = input<number | string>(0, {alias: 'msRemainingCharacters'});

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly formFieldComponent = inject(FormFieldComponent, {host: true});
  private readonly destroyRef = inject(DestroyRef);
  private readonly counterElement: HTMLElement = this.renderer.createElement('span');

  private textElement?: HTMLInputElement | HTMLTextAreaElement;
  private resolvedMaxLength = 0;
  private remaining?: number;

  constructor() {
    this.formFieldComponent.setHasCharacterCounter(true);
    this.renderer.addClass(this.counterElement, 'ms-remaining-characters-wrapper');

    afterNextRender(() => {
      this.init();
    });

    effect(() => {
      const maxLength = Number(this.maxLength());

      if (!maxLength) {
        return;
      }

      this.resolvedMaxLength = maxLength;
      this.updateCounter();
    });
  }

  updateCounter() {
    if (!this.textElement || !this.resolvedMaxLength) {
      return;
    }
    if (this.textElement.disabled) {
      this.renderer.addClass(this.counterElement, 'disabled');
    } else {
      this.renderer.removeClass(this.counterElement, 'disabled');
    }

    this.remaining = this.resolvedMaxLength - this.textElement.value.length;

    this.renderer.setProperty(
      this.counterElement,
      'innerHTML',
      translate('VALIDATORS.REMAINING_CHARACTERS', {
        remaining: this.remaining,
      })
    );
  }

  private init(): void {
    const placeholder = this.formFieldComponent.charactersCounter()?.nativeElement;

    if (!placeholder) {
      return;
    }

    this.textElement = this.hostElement.nativeElement.querySelector('input, textarea') ?? undefined;

    if (!this.textElement) {
      return;
    }

    const control = this.formFieldComponent.control;
    control?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updateCounter());

    this.renderer.appendChild(placeholder, this.counterElement);
    this.renderer.addClass(placeholder, 'ms-remaining-characters-wrapper');

    if (!this.resolvedMaxLength) {
      this.resolvedMaxLength = Number(this.maxLength()) || Number(this.textElement.getAttribute('maxlength'));
    }

    const removeInputListener = this.renderer.listen(this.textElement, 'input', () => {
      if (this.shouldUpdate(this.textElement?.value.length ?? 0)) {
        this.updateCounter();
      }
    });

    this.destroyRef.onDestroy(removeInputListener);

    this.updateCounter();
  }

  private shouldUpdate(currentInputLength: number): boolean {
    return this.remaining === undefined || this.remaining !== this.resolvedMaxLength - currentInputLength;
  }
}
